import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { memo } from 'react';

import { PendingPosition } from '@flipster/r230522/original/order';
import { SpotPendingPosition } from '@flipster/r230522/original/spot-order';
import { flex } from '@flipster/styled-system/patterns';
import Num, { BigNumber } from '@flipster/utils/num';

import { OrderbookDisplayMode } from '@web/app/[locale]/(web)/trade/_misc/state';
import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { OrderbookItem, OrderbookRow, rowHeight } from './OrderbookRow';

interface OrderbookRowsProps {
  items: OrderbookItem[];
  type: 'ask' | 'bid';
  maxSize: number;
  hoveredIndex: number | null;
  onMouseEnter: (index: number) => void;
  onMouseLeave: () => void;
  onClick: (index: number) => void;
  pendingPositions: PendingPosition[] | SpotPendingPosition[];
  displayMode: OrderbookDisplayMode;
  selectedTickSize: string;
}

export const OrderbookRows = memo(function OrderbookRows({
  items,
  type,
  maxSize,
  hoveredIndex,
  onMouseEnter,
  onMouseLeave,
  onClick,
  pendingPositions,
  displayMode,
  selectedTickSize,
}: OrderbookRowsProps) {
  const { symbolInfoAtom } = useBunja(tradeSymbolBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);

  if (!symbolInfo) return null;

  const tick = Num(selectedTickSize);
  const getBucketPrice = (price: string) => {
    if (tick.isZero() || tick.isNaN()) return price;
    const p = Num(price);
    const method = type === 'bid' ? BigNumber.ROUND_FLOOR : BigNumber.ROUND_CEIL;
    return p.dividedBy(tick).integerValue(method).times(tick).toString();
  };

  return (
    <div
      className={flex({ flexDirection: type === 'ask' ? 'column-reverse' : 'column', h: items.length * rowHeight })}
      onMouseLeave={onMouseLeave}
    >
      {items.map((item, index) => (
        <OrderbookRow
          // 가격을 key로 쓰면 레벨 창이 밀릴 때마다 row가 재마운트되므로 슬롯(index) 고정 key 사용
          key={`${type}-${index}`}
          currency={symbolInfo.isSpot ? symbolInfo.baseCurrency : symbolInfo.currency}
          quoteCurrency={symbolInfo.quoteCurrency}
          precision={symbolInfo.pricePrecision}
          qtyPrecision={symbolInfo.tradeQtyPrecision}
          item={item}
          type={type}
          index={index}
          percentage={maxSize > 0 ? (item.sum?.toNumber() || 0) / maxSize : 0}
          isHovered={hoveredIndex !== null && index <= hoveredIndex}
          isActiveHover={hoveredIndex === index}
          onMouseEnter={onMouseEnter}
          onClick={onClick}
          isMarked={pendingPositions.some(
            (o) =>
              o.price &&
              item.price &&
              (type === 'bid' ? o.side === 'Long' : o.side === 'Short') &&
              Num(getBucketPrice(o.price)).isEqualTo(item.price),
          )}
          displayMode={displayMode}
        />
      ))}
    </div>
  );
});
